/**
 * Select Field Component
 * Wrapper for MUI Select with react-hook-form integration
 */
import React, { useState, useMemo, useEffect } from 'react'
import {
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  CircularProgress
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import { Controller } from 'react-hook-form'
import { FormFieldProps, FieldOption } from '../types'
import { useFormContext } from '../FormContext'
import { buildValidationRules } from '../utils/validationRules'

const SelectField: React.FC<FormFieldProps> = ({
  config,
  control,
  errors,
  readOnly
}) => {
  const {
    name,
    label,
    options = [],
    required,
    disabled,
    helperText,
    placeholder,
    dataSource,
    fieldProps = {}
  } = config

  // state for tracking mouse hover
  const [isHovering, setIsHovering] = useState(false)

  // get the original values from the form context for change detection
  const formContext = useFormContext()
  const formValues = formContext?.values || {}

  // get the data source data
  const dataSources = formContext?.dataSources || {}
  const dataSourceState = dataSource ? dataSources[dataSource] : null

  // merge the options: use the options from the data source first, if there is no data source or the data source is not loaded, use the options from the config
  const mergedOptions = useMemo<FieldOption[]>(() => {
    if (
      dataSource &&
      dataSourceState?.options &&
      dataSourceState.options.length > 0
    ) {
      return dataSourceState.options
    }
    return options
  }, [dataSource, dataSourceState, options])

  // 从fieldProps中提取MUI TextField的属性
  const {
    allowClear,
    InputProps: fieldInputProps = {},
    InputLabelProps,
    sx: fieldSx,
    ...otherTextFieldProps
  } = fieldProps

  const isFieldChanged = () => {
    // check the field level configuration, if the change highlighting is not enabled, return false
    if (config.enableChangeHighlight !== true) {
      return false
    }

    // if the change highlighting is enabled, check if the field is changed
    if (formContext) {
      const originalValues = formContext.originalValues || {}
      const currentValues = formValues

      // compare the original value and the current value
      const originalValue = originalValues[name]
      const currentValue = currentValues[name]

      // handle the select value
      const origSelectValue = originalValue?.toString() || ''
      const currSelectValue = currentValue?.toString() || ''

      // only return true when the value is really different
      return origSelectValue !== currSelectValue
    }
    return false
  }

  // check if the field is changed
  const isChanged = isFieldChanged()

  // check if the field is read only
  const isReadOnly =
    typeof readOnly === 'boolean'
      ? readOnly
      : Array.isArray(readOnly)
        ? readOnly.includes(name)
        : false

  // check if the data source is loading
  const isLoading = dataSource && dataSourceState?.loading === true

  // 合并样式
  const mergedSx = {
    '& .MuiInputBase-root': {
      height: '36px'
    },
    '& .MuiSelect-select': {
      paddingRight: allowClear ? '48px' : '32px', // reserve space for the clear icon
      ...(isChanged && {
        color: 'secondary.dark',
        fontWeight: 'bold'
      })
    },
    ...(fieldSx || {})
  }

  // Check if the current value exists in the options
  const isValueInOptions = (value: any) => {
    if (!value) return true // Empty value is always valid
    return mergedOptions.some(option => option.value === value)
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={buildValidationRules(config)}
      render={({ field }) => {
        // Ensure field.value is never undefined (always a string)
        const fieldValue = field.value === undefined ? '' : field.value

        // Check if the current value is valid in the current options
        const isCurrentValueValid = isValueInOptions(fieldValue)

        // Use effect to handle value when options change
        useEffect(() => {
          // If we have a value but it's not in the options and options are loaded
          if (
            fieldValue &&
            !isCurrentValueValid &&
            mergedOptions.length > 0 &&
            !isLoading
          ) {
            // If the data source is loaded but the value is not valid, clear it
            field.onChange('')
          } else if (field.value === undefined) {
            // Ensure we always have a controlled component by setting empty string
            // when the value is undefined
            field.onChange('')
          }
        }, [mergedOptions, isCurrentValueValid, isLoading, field.value])

        // handle the clear option
        const handleClear = (e: React.MouseEvent) => {
          e.stopPropagation() // prevent the dropdown menu from being triggered
          field.onChange('') // clear the value
        }

        // handle the mouse hover event
        const handleMouseEnter = () => {
          setIsHovering(true)
        }

        const handleMouseLeave = () => {
          setIsHovering(false)
        }

        // create the clear button
        const clearButton =
          allowClear && fieldValue ? (
            <InputAdornment
              position='end'
              sx={{
                visibility: isHovering ? 'visible' : 'hidden',
                position: 'absolute',
                right: '28px', // put the clear icon on the left of the dropdown icon
                pointerEvents: 'auto' // ensure it is clickable
              }}>
              <IconButton
                size='small'
                onClick={handleClear}
                sx={{
                  padding: '2px',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)'
                  }
                }}>
                <ClearIcon fontSize='small' />
              </IconButton>
            </InputAdornment>
          ) : undefined

        // create the loading indicator
        const loadingIndicator = isLoading ? (
          <InputAdornment
            position='end'
            sx={{
              position: 'absolute',
              right: allowClear ? '48px' : '28px',
              pointerEvents: 'none'
            }}>
            <CircularProgress size={16} />
          </InputAdornment>
        ) : undefined

        // merge the InputProps
        const mergedInputProps = {
          ...fieldInputProps,
          endAdornment: (
            <>
              {loadingIndicator}
              {clearButton}
              {fieldInputProps.endAdornment}
            </>
          )
        }

        // handle the data source error
        const hasError =
          Boolean(errors[name]) || Boolean(dataSource && dataSourceState?.error)
        const errorMessage =
          errors[name]?.message ||
          (dataSource && dataSourceState?.error
            ? dataSourceState.error?.message ||
              dataSourceState.error?.msg ||
              dataSourceState.error ||
              'Failed to load options'
            : helperText)

        // Handle the value to prevent "out-of-range" warnings
        // Always use empty string instead of undefined to maintain controlled state
        const safeValue = isLoading || !isCurrentValueValid ? '' : fieldValue

        return (
          <TextField
            {...field}
            value={safeValue}
            select
            label={label}
            placeholder={placeholder || otherTextFieldProps.placeholder}
            variant='outlined'
            size='small'
            fullWidth
            required={required}
            disabled={disabled || isReadOnly || isLoading === true}
            error={hasError}
            helperText={errorMessage}
            sx={mergedSx}
            InputProps={mergedInputProps}
            InputLabelProps={InputLabelProps}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            {...otherTextFieldProps}>
            {mergedOptions.map((option: FieldOption) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        )
      }}
    />
  )
}

export default SelectField
